import { Card, CardContent } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '@/lib/auth';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
};

export function SimulationView({ data }) {
    const { isAdminFull, isAdmin } = useAuth();
    // Seuls Admin Full et Admin peuvent voir les pourcentages
    const canSeePercent = isAdminFull?.() || isAdmin?.();
    
    if (!data) return null;
    
    return (
        <div className="space-y-4 py-4">
            {!data.is_valid && (
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>{data.validation_errors?.join(', ')}</AlertDescription>
                </Alert>
            )}
            
            <div className="grid grid-cols-3 gap-3">
                <Card className="bg-slate-50">
                    <CardContent className="pt-3 text-center">
                        <p className="text-xs">Global</p>
                        <p className="text-lg font-bold">{formatCurrency(data.montant_global)}</p>
                    </CardContent>
                </Card>
                <Card className="bg-purple-50">
                    <CardContent className="pt-3 text-center">
                        <p className="text-xs text-purple-600">Valnexo</p>
                        <p className="text-lg font-bold">{formatCurrency(data.base_valnexo)}</p>
                    </CardContent>
                </Card>
                <Card className="bg-indigo-50">
                    <CardContent className="pt-3 text-center">
                        <p className="text-xs text-indigo-600">AT Service</p>
                        <p className="text-lg font-bold">{formatCurrency(data.base_at_service)}</p>
                    </CardContent>
                </Card>
            </div>
            
            <Card>
                <CardContent className="pt-3">
                    <p className="text-sm font-medium mb-2">
                        Commission VP {canSeePercent && `(${data.vp_commission_percent}%)`}
                    </p>
                    <div className="grid grid-cols-3 gap-3">
                        <div className="p-2 bg-emerald-50 rounded text-center">
                            <p className="text-xs">Total VP</p>
                            <p className="font-bold">{formatCurrency(data.vp_commission_montant)}</p>
                        </div>
                        <div className="p-2 bg-purple-50 rounded text-center">
                            <p className="text-xs">Par Valnexo</p>
                            <p className="font-bold">{formatCurrency(data.vp_paid_by_valnexo)}</p>
                        </div>
                        <div className="p-2 bg-indigo-50 rounded text-center">
                            <p className="text-xs">Par AT</p>
                            <p className="font-bold">{formatCurrency(data.vp_paid_by_at)}</p>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {data.apporteurs?.length > 0 && (
                <ApporteursCard apporteurs={data.apporteurs} total={data.total_apporteurs} canSeePercent={canSeePercent} />
            )}
            
            <FinalResultCard data={data} />
        </div>
    );
}

function ApporteursCard({ apporteurs, total, canSeePercent }) {
    return (
        <Card>
            <CardContent className="pt-3">
                <p className="text-sm font-medium mb-2">Apporteurs (déduits VP)</p>
                {apporteurs.map((a, i) => {
                    if (a.montant <= 0) return null;
                    return (
                        <div key={i} className="flex justify-between py-1 border-b last:border-0">
                            <span>{a.apporteur_name} {canSeePercent && `(${a.commission_percent}%)`}</span>
                            <span className="font-bold">{formatCurrency(a.montant)}</span>
                        </div>
                    );
                })}
                <div className="flex justify-between pt-2 font-bold">
                    <span>Total</span>
                    <span>{formatCurrency(total)}</span>
                </div>
            </CardContent>
        </Card>
    );
}

function FinalResultCard({ data }) {
    return (
        <Card className="border-2">
            <CardContent className="pt-3">
                <p className="text-sm font-medium mb-2">Résultat Final</p>
                <div className="grid grid-cols-4 gap-2">
                    <div className="p-2 bg-purple-100 rounded text-center">
                        <p className="text-xs">Valnexo Net</p>
                        <p className="font-bold">{formatCurrency(data.valnexo_net)}</p>
                    </div>
                    <div className="p-2 bg-indigo-100 rounded text-center">
                        <p className="text-xs">AT Net</p>
                        <p className="font-bold">{formatCurrency(data.at_service_net)}</p>
                    </div>
                    <div className="p-2 bg-emerald-100 rounded text-center">
                        <p className="text-xs">VP Net</p>
                        <p className="font-bold">{formatCurrency(data.vp_net)}</p>
                    </div>
                    <div className="p-2 bg-sky-100 rounded text-center">
                        <p className="text-xs">Apporteurs</p>
                        <p className="font-bold">{formatCurrency(data.total_apporteurs)}</p>
                    </div>
                </div>
                <p className="text-center mt-3 text-sm">
                    Total: <span className="font-bold text-green-600">{formatCurrency(data.total_check)}</span> = {formatCurrency(data.montant_global)} ✓
                </p>
            </CardContent>
        </Card>
    );
}
